import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IApiRequest, IMaterialsResponse } from "@miplatform/types";

export const useMaterials = () => {
  const [materials, setMaterials] = useState<IMaterialsResponse[]>([]);
  const [refresh, setRefresh] = useState(false);

  const stateManager = useComponentState();

  const getMaterials = ({ top, skip, filters }: IApiRequest = {}) => {
    return stateManager.run(() => {
      return homeService.materials.getAll({
        sort: { category: ["categoryName", "itemCode"] },
        top,
        skip,
        filters,
      });
    });
  };

  const getMaterialDetails = (id: string) => {
    return stateManager.run(() => {
      return homeService.materials.getById(id);
    });
  };

  const getMaterialPatternAutoGenerator = (data: IMaterialsResponse) => {
    return stateManager.run(() => {
      return homeService.materials.getAutoGen({ data });
    });
  };

  const createMaterial = (data: IMaterialsResponse) => {
    return stateManager.run(() => {
      return homeService.materials.create({
        data,
        toastMessageConfig: {
          success: {
            text: `Successfully created Item: ${data.category.itemCode} | ${data.category.itemDescription}.`,
          },
        },
      });
    });
  };

  const updateMaterial = (data: Partial<IMaterialsResponse>) => {
    return stateManager.run(() => {
      return homeService.materials.update({
        id: data.id,
        data,
        toastMessageConfig: {
          success: {
            text: `Successfully updated Item: ${data.category?.itemCode} | ${data.category?.itemDescription}.`,
          },
        },
      });
    });
  };

  const deleteMaterial = (data: IMaterialsResponse) => {
    stateManager.run(async () => {
      const response = await homeService.materials.delete({
        id: data.id,
        modifiedOn: data.modifiedOn,
        toastMessageConfig: {
          success: {
            text: `${data.category.itemCode} | ${data.category.itemDescription} was deleted successfully.`,
          },
          failure: {
            text: `Failed to delete ${data.category.itemCode} | ${data.category.itemDescription}.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setRefresh((prev) => !prev);
      }
    });
  };

  return {
    isLoading: stateManager.isBusy(),
    materials,
    refresh,
    getMaterials,
    getMaterialDetails,
    getMaterialPatternAutoGenerator,
    setMaterials,
    createMaterial,
    updateMaterial,
    deleteMaterial,
  };
};
